import React from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Dropdown, Flex, Typography, Tooltip } from "antd"
import { capitalize, isEqual } from "lodash"
import PropTypes from "prop-types"
import { PrimeButton, Breadcrumbs } from "components-design-system"
import Modal from "components/Modal/Modal"
import TrackerStatus from "../TrackerStatus/TrackerStatus"
import { formatButtonText } from "../../WorkflowModalForm/utils/formatButtonText"

// context
import useWorkflowTracker from "context/hooks/useWorkflowTracker"

// apis
import { useDeleteRequest } from "api/hooks/requests/useRequestsApi"
import { useDeleteActivity } from "api/hooks/activities/useActivitiesApi"
import { useGetRequestApprovalRecords } from "api/hooks/requests/requestReviewApis"
import { useGetWorkflow } from "api/hooks/workflows/useWorkflowsApi"
import { useUserPermissions } from "api/hooks"

// styles
import "./TrackerHeader.scss"

const { Title } = Typography

const TrackerHeader = ({
  model,
  title,
  status,
  breadcrumbs = [],
  workflowId,
  workflowObj,
  showDetails, // for activity only
  setShowDetails, // for activity only
  actions = [], // is favorite for request
  showWorkFlowActions,
  onOpenWorkflowModal,
  style = {},
}) => {
  // server data
  const navigate = useNavigate()
  const { requestId, studyId, activityId } = useParams()
  const [showDelete, setShowDelete] = React.useState(false)
  const [loadingDelete, setLoadingDelete] = React.useState(false)
  const { data: workflowTracker } = useGetWorkflow({
    workflowId,
    workflowModel: model,
  })

  const { data: permissions } = useUserPermissions()

  const {
    workflowTrackerData,
    completedStates,
    isWorkflowCancelled,
    isOnHold,
    currentState,
    isWorkflowComplete,
    setOpenForceModal,
    setOpenEditStatesPanel,
    setOpenCancelWorkflowModal,
    invalidateParentQueries,
    holdWorkflow,
  } = useWorkflowTracker()
  const { data: records = [] } = useGetRequestApprovalRecords(requestId)
  const deleteRequest = useDeleteRequest()
  const deleteActivity = useDeleteActivity(studyId, activityId)

  const hasEditPermission = model === "activity" ? permissions?.["project.edit"] : permissions?.["request.edit"]
  const hasDeletePermission = model === "activity" ? permissions?.["project.delete"] : permissions?.["request.delete"]

  const hasProgressPermission = permissions?.["workflow.progress"]

  const items = React.useMemo(() => {
    let options = [
      {
        key: "edit",
        label: <a onClick={() => setOpenEditStatesPanel(true)}>Edit steps and due dates</a>,
      },
    ]

    if (completedStates?.length > 0 && !isWorkflowComplete) {
      if (!workflowObj?.actual_completion_date) {
        options.push({
          key: "force",
          disabled: !hasProgressPermission,
          label: <a onClick={() => setOpenForceModal("force")}>Force step</a>,
        })
      }
      if (!isOnHold && !workflowObj?.actual_completion_date) {
        options.push({
          key: "hold",
          disabled: !hasProgressPermission,
          label: (
            <a
              onClick={() =>
                holdWorkflow(
                  {
                    body: { model },
                  },
                  {
                    onSuccess: () => {
                      invalidateParentQueries()
                    },
                  }
                )
              }
            >
              Hold {model}
            </a>
          ),
        })
      }
      if (!workflowObj?.actual_completion_date) {
        options.push({
          key: "cancel",
          disabled: !hasProgressPermission,
          label: <a onClick={() => setOpenCancelWorkflowModal(true)}>Cancel {model}</a>,
        })
      }
    }

    options.push({
      key: "delete",
      disabled: !hasDeletePermission,
      label: (
        <a
          onClick={() => {
            setShowDelete(true)
          }}
        >
          Delete {model}
        </a>
      ),
    })

    return options
  }, [completedStates, model, permissions])

  const deleteMessage = React.useMemo(() => {
    if (model === "request") {
      if (records.length > 0) {
        return "Deleting this request will also remove linked approvals and action items. This action cannot be undone"
      } else {
        return "Deleting this request removes all related action items. This action cannot be undone"
      }
    } else {
      return "Deleting the activity will unassign all team members and delete all action items that have not been cancelled or completed"
    }
  }, [records, model])

  const onDeleteObj = () => {
    setLoadingDelete(true)
    if (model === "request") {
      deleteRequest(
        { id: `${requestId}/` },
        {
          onSuccess: () => {
            navigate("/requests")
          },
          onSettled: () => {
            setLoadingDelete(false)
          },
        }
      )
    } else {
      deleteActivity(
        {},
        {
          onSuccess: () => {
            navigate(`/study/${studyId}/overview`)
          },
          onSettled: () => {
            setLoadingDelete(false)
          },
        }
      )
    }
  }

  const renderResendActions = () => {
    if (!currentState?.allow_resend) return

    let lastActionsArr = []

    const foundLastAction = resendActions.find((action) => action.is_last_action)
    if (foundLastAction) {
      lastActionsArr.push(foundLastAction)
    } else {
      lastActionsArr = resendActions
    }

    if (lastActionsArr.length === 1) {
      const action = lastActionsArr[0]
      return (
        <Tooltip
          title={hasProgressPermission ? action?.description : "You do not have permission to perform this action"}
          key={action?.description}
        >
          <PrimeButton
            key={action.id}
            disabled={!hasProgressPermission}
            icon={<i className="fal fa-arrow-to-right" />}
            onClick={() => onOpenWorkflowModal({ stepId: action.id, isResend: true })}
          >
            Resend to {formatButtonText(currentState?.state_title)} ({formatButtonText(action.button_text)})
          </PrimeButton>
        </Tooltip>
      )
    } else {
      return (
        <Dropdown
          menu={{
            items: lastActionsArr.map((action) => ({
              label: (
                <a onClick={() => onOpenWorkflowModal({ stepId: action.id, isResend: true })}>
                  {formatButtonText(action.button_text)}
                </a>
              ),
              key: action.id,
            })),
          }}
          placement="bottomRight"
        >
          <PrimeButton
            // key={action.id}
            disabled={!hasProgressPermission}
            icon={<i className="fal fa-arrow-to-right" />}
            // onClick={() => onOpenWorkflowModal({ stepId: action.id, isResend: true })}
          >
            Resend to {formatButtonText(currentState?.state_title)}
          </PrimeButton>
        </Dropdown>
      )
    }

    // return lastActionsArr.map((action) => {
    //   return (
    //     <Tooltip
    //       title={hasProgressPermission ? action?.description : "You do not have permission to perform this action"}
    //       key={action?.description}
    //     >
    //       <PrimeButton
    //         key={action.id}
    //         disabled={!hasProgressPermission}
    //         icon={<i className="fal fa-arrow-to-right" />}
    //         onClick={() => onOpenWorkflowModal({ stepId: action.id, isResend: true })}
    //       >
    //         Resend to {formatButtonText(currentState?.state_title)} ({formatButtonText(action.button_text)})
    //       </PrimeButton>
    //     </Tooltip>
    //   )
    // })
  }

  const resendActions = workflowTrackerData.prev_actions

  return (
    <Flex
      className="tracker-header"
      justify="space-between"
      align="end"
      gap={6}
      style={style}
    >
      <div className="left-header">
        {breadcrumbs && <Breadcrumbs data={breadcrumbs} />}
        <Flex
          align="center"
          gap={6}
          wrap
        >
          {setShowDetails && (
            <div className="caret-button">
              {showDetails ? (
                <i
                  className="fal fa-angle-up"
                  onClick={() => setShowDetails(false)}
                />
              ) : (
                <i
                  className="fal fa-angle-down"
                  onClick={() => setShowDetails(true)}
                />
              )}
            </div>
          )}
          <Title level={3}>{title}</Title>
          <TrackerStatus
            model={model}
            status={status}
          />
          <div className="header-actions">{actions}</div>
        </Flex>
      </div>
      {showWorkFlowActions && (
        <div className="right-header">
          {/* OTHER ACTION BUTTONS  */}
          {(hasEditPermission || hasDeletePermission) && isWorkflowCancelled !== true && (
            <Dropdown
              menu={{ items }}
              placement="bottomRight"
            >
              <PrimeButton icon={<i className="fal fa-ellipsis-v" />} />
            </Dropdown>
          )}

          {/* MAJOR/MIOR PUSH STEP BUTTONS */}
          {workflowTracker?.next_actions &&
            workflowTracker?.next_actions.length > 0 &&
            workflowTracker?.next_actions
              .sort((a, b) => a.is_next_state_major - b.is_next_state_major)
              .map((action) => (
                <Tooltip
                  title={
                    hasProgressPermission ? action?.description : "You do not have permission to perform this action"
                  }
                  key={action?.description}
                >
                  <PrimeButton
                    type={action.is_next_state_major ? "primary" : "default"}
                    icon={<i className="fal fa-arrow-to-right" />}
                    disabled={!hasProgressPermission}
                    onClick={() => {
                      onOpenWorkflowModal({ stepId: action.id })
                    }}
                  >
                    {formatButtonText(action.button_text)}
                  </PrimeButton>
                </Tooltip>
              ))}

          {/* RESEND BUTTON */}
          {renderResendActions()}

          {/* ON HOLD BUTTON */}
          {isOnHold && (
            <PrimeButton
              key="unhold-button"
              type="primary"
              disabled={!hasProgressPermission}
              icon={<i className="fal fa-arrow-to-right" />}
              onClick={() => setOpenForceModal("unhold")}
            >
              Unhold
            </PrimeButton>
          )}
        </div>
      )}
      <Modal
        title={`Delete ${capitalize(model)}`}
        message={deleteMessage}
        open={showDelete}
        type="delete"
        onOk={onDeleteObj}
        onCancel={() => setShowDelete(false)}
        okButtonProps={{
          loading: loadingDelete,
        }}
      />
    </Flex>
  )
}

TrackerHeader.propTypes = {
  model: PropTypes.oneOf(["activity", "request"]),
  title: PropTypes.string,
  status: PropTypes.string,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  workflowId: PropTypes.number,
  workflowObj: PropTypes.object, // activity || request obj
  showDetails: PropTypes.bool,
  setShowDetails: PropTypes.func,
  actions: PropTypes.arrayOf(PropTypes.element),
  showWorkFlowActions: PropTypes.bool,
  onOpenWorkflowModal: PropTypes.func,
  style: PropTypes.object,
}

export default TrackerHeader
